import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  Tooltip
} from '@material-ui/core';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism.css';

const FONT_SIZE = '13px';
const FONT_FAMILY = 'monospace';
const HISTORY_LIMIT = 50;
const HISTORY_DEBOUNCE_MS = 800;

const isXmlValid = (xmlString) => {
  if (!xmlString) return false;
  const doc = new DOMParser().parseFromString(xmlString, 'application/xml');
  return doc.documentElement && !doc.querySelector('parsererror');
};

const attrsToObject = (element) =>
  Array.from(element.attributes).reduce((acc, a) => { acc[a.name] = a.value; return acc; }, {});

const parseXmlToEntries = (xmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, 'application/xml');
  const root = doc.documentElement;

  if (!root || doc.querySelector('parsererror')) {
    throw new Error('Invalid XML received from server.');
  }

  const entries = [];
  let seq = 0;

  Array.from(root.children).forEach(child => {
    if (child.tagName === 'LOAN') {
      const loanNumber = child.getAttribute('LoanNumber');
      Array.from(child.children).forEach(detail => {
        entries.push({ id: seq++, element: detail.tagName, loanNumber, attrs: attrsToObject(detail) });
      });
    } else if (child.tagName === 'TRANSACTIONS') {
      Array.from(child.children).forEach(txn => {
        entries.push({ id: seq++, element: txn.tagName, loanNumber: null, attrs: attrsToObject(txn) });
      });
    }
  });

  return entries;
};

const ReadableEntry = ({ entry, isEven }) => (
  <div style={{
    padding: '10px 14px',
    backgroundColor: isEven ? '#ffffff' : '#f5f7fa',
    borderBottom: '1px solid #e0e0e0',
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZE,
  }}>
    <div style={{ marginBottom: 6 }}>
      <span style={{ color: '#1a237e', fontWeight: 'bold' }}>{entry.element}</span>
      {entry.loanNumber && (
        <span style={{ color: '#555', marginLeft: 8 }}>· {entry.loanNumber}</span>
      )}
    </div>
    {Object.entries(entry.attrs).map(([k, v]) => (
      <div key={k} style={{ display: 'flex', paddingLeft: 16, lineHeight: '1.7' }}>
        <span style={{ color: '#b52020', minWidth: 170 }}>{k}</span>
        <span style={{ color: '#333' }}>{v}</span>
      </div>
    ))}
  </div>
);

ReadableEntry.propTypes = {
  entry: PropTypes.shape({
    id:         PropTypes.number.isRequired,
    element:    PropTypes.string.isRequired,
    loanNumber: PropTypes.string,
    attrs:      PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
  isEven: PropTypes.bool.isRequired,
};

const NLSFilePreviewModal = ({ open, onClose, onConfirm, fetchContent }) => {
  const [fileContent, setFileContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [debouncedContent, setDebouncedContent] = useState('');
  const historyRef = useRef([]);
  const pendingSnapshotRef = useRef(true);
  const historyTimerRef = useRef(null);

  const setFileContentWithHistory = useCallback((next) => {
    setFileContent(prev => {
      if (pendingSnapshotRef.current) {
        historyRef.current.push(prev);
        if (historyRef.current.length > HISTORY_LIMIT) {
          historyRef.current = historyRef.current.slice(-HISTORY_LIMIT);
        }
        pendingSnapshotRef.current = false;
      }
      clearTimeout(historyTimerRef.current);
      historyTimerRef.current = setTimeout(() => {
        pendingSnapshotRef.current = true;
      }, HISTORY_DEBOUNCE_MS);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && historyRef.current.length > 0) {
        e.preventDefault();
        const prev = historyRef.current.pop();
        setFileContent(prev);
        pendingSnapshotRef.current = true;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setError(null);
      setFileContent('');
      setDebouncedContent('');
      historyRef.current = [];
      pendingSnapshotRef.current = true;
      clearTimeout(historyTimerRef.current);
      setActiveTab(0);
      fetchContent()
        .then(content => {
          const trimmed = typeof content === 'string' ? content.trim() : '';
          setFileContent(trimmed.startsWith('<') ? trimmed : '');
        })
        .catch(() => setError('Failed to load file preview.'))
        .finally(() => setIsLoading(false));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedContent(fileContent), 600);
    return () => clearTimeout(timer);
  }, [fileContent]);

  const xmlValid = useMemo(() => isXmlValid(fileContent), [fileContent]);

  const { entries, readableParseError } = useMemo(() => {
    if (isLoading || error || !debouncedContent) return { entries: [], readableParseError: false };
    try {
      return { entries: parseXmlToEntries(debouncedContent), readableParseError: false };
    } catch {
      return { entries: [], readableParseError: true };
    }
  }, [isLoading, error, debouncedContent]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Review and Send XML File</DialogTitle>
      <DialogContent>
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <CircularProgress />
          </div>
        )}
        {error && (
          <Typography color="error">{error}</Typography>
        )}
        {!isLoading && !error && !fileContent && (
          <Typography style={{ padding: '16px' }}>No file content available.</Typography>
        )}
        {!isLoading && !error && fileContent && (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="XML" />
              <Tab label="Readable" />
            </Tabs>
            {activeTab === 0 && (
              <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e0e0e0' }}>
                <Editor
                  value={fileContent}
                  onValueChange={setFileContentWithHistory}
                  highlight={code => highlight(code, languages.markup, 'xml')}
                  padding={8}
                  style={{
                    fontSize: FONT_SIZE,
                    fontFamily: FONT_FAMILY,
                    minHeight: '200px',
                  }}
                  textareaProps={{}}
                />
              </div>
            )}
            {activeTab === 1 && (
              readableParseError ? (
                <Typography color="error" style={{ padding: '16px' }}>XML is not valid - fix it in the XML tab to see the Readable view.</Typography>
              ) : entries.length === 0 ? (
                <Typography style={{ padding: '16px' }}>No records to display.</Typography>
              ) : (
                <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e0e0e0' }}>
                  {entries.map((entry, i) => (
                    <ReadableEntry key={entry.id} entry={entry} isEven={i % 2 === 0} />
                  ))}
                </div>
              )
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Tooltip title={!xmlValid && fileContent ? 'XML is invalid - fix errors before sending' : ''}>
          <span>
            <Button
              onClick={() => onConfirm(fileContent)}
              color="secondary"
              variant="contained"
              disabled={isLoading || !!error || !fileContent || !xmlValid}
              autoFocus
            >
              Send
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

NLSFilePreviewModal.propTypes = {
  open:         PropTypes.bool.isRequired,
  onClose:      PropTypes.func.isRequired,
  onConfirm:    PropTypes.func.isRequired,
  fetchContent: PropTypes.func.isRequired,
};

export default NLSFilePreviewModal;
